import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, X } from "lucide-react";

export default function SyncProgressModal({ jobId, onClose }) {
    const [job, setJob] = useState(null);
    const pollRef = useRef(null);
    const stoppedRef = useRef(false);

    useEffect(() => {
        if (!jobId) return;
        const poll = async () => {
            try {
                const { data } = await api.get(`/sync-jobs/${jobId}`);
                setJob(data);
                if (data.status === "success" || data.status === "failed") {
                    if (pollRef.current) clearInterval(pollRef.current);
                    stoppedRef.current = true;
                }
            } catch (e) {
                if (pollRef.current) clearInterval(pollRef.current);
            }
        };
        poll();
        pollRef.current = setInterval(poll, 1500);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [jobId]);

    if (!jobId) return null;
    const done = job?.status === "success";
    const failed = job?.status === "failed";
    const pct = job?.progress_pct || 0;

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="sync-progress-modal">
            <div className="bg-zinc-50 border border-zinc-200 w-full max-w-lg p-6 rounded-md">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        {done ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                        ) : failed ? (
                            <XCircle className="h-5 w-5 text-red-400" />
                        ) : (
                            <Loader2 className="h-5 w-5 wl-accent-text animate-spin" />
                        )}
                        <div>
                            <div className="overline">Background sync</div>
                            <h3 className="font-display text-lg">
                                {done ? "Sync complete" : failed ? "Sync failed" : "Syncing…"}
                            </h3>
                        </div>
                    </div>
                    {(done || failed) && (
                        <button onClick={onClose}><X className="h-4 w-4 text-zinc-500" /></button>
                    )}
                </div>
                <div className="mb-3">
                    <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                        <div
                            className="h-full transition-all duration-500"
                            style={{
                                width: `${pct}%`,
                                background: failed ? "#ef4444" : "var(--wl-primary-hex)",
                            }}
                        />
                    </div>
                    <div className="flex justify-between mt-1 text-[11px] text-zinc-500">
                        <span data-testid="sync-current-step">{job?.current_step || "Initializing…"}</span>
                        <span className="tabular-nums">{pct}%</span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-white border border-zinc-200 p-3">
                        <div className="overline">Accounts</div>
                        <div className="font-display text-lg tabular-nums mt-1">
                            {job?.accounts_done || 0}<span className="text-zinc-400 text-sm"> / {job?.accounts_total || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-zinc-200 p-3">
                        <div className="overline">Campaigns</div>
                        <div className="font-display text-lg tabular-nums mt-1">
                            {job?.campaigns_done || 0}<span className="text-zinc-400 text-sm"> / {job?.campaigns_total || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-zinc-200 p-3">
                        <div className="overline">Metric rows</div>
                        <div className="font-display text-lg tabular-nums mt-1">{job?.metric_rows || 0}</div>
                    </div>
                </div>
                {failed && job?.error && (
                    <div className="mt-3 text-[11px] text-red-400 bg-red-500/5 border border-red-500/20 p-2 rounded font-mono">
                        {job.error}
                    </div>
                )}
                {!done && !failed && (
                    <p className="mt-3 text-[11px] text-zinc-500">
                        You can close this window — the sync continues in the background. Open again to check progress.
                    </p>
                )}
                <div className="flex justify-end mt-4">
                    <button onClick={onClose} className="px-4 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100" data-testid="sync-close-btn">
                        {done || failed ? "Close" : "Run in background"}
                    </button>
                </div>
            </div>
        </div>
    );
}
